import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import {
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_PARTNER_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_404,
  ROUTE_NO_APP_FOUND,
} from '@/constants/routes';
import { LOCATIONS_MOVIE_DETAILS_PAGE } from '@/constants/locations';

// UTILITY
import { analyticsPageLoaded, analyticsAddedToWatchlist } from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';
import getLink, { routeEnhance } from '@/utility/routes';
import { getSearchParams } from '@/utility/common';
import store from '@/utility/store';
import { getS3ImageUrl } from '@/utility/content';

// STORE
import { AppActions, ContentActions, MovieDetailsActions, WatchListActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';
import withMediaQuery from '@/hoc/withMediaQuery';

// COMPONENTS
import SimilarContent from '@/components/SimilarContent';
import ContentGallery from '@/components/ContentGallery';
import NotifyMe from '@/components/NotifyMe';
import Redirect from '@/components/Redirect';
import DialogDownloadApp from '@/components/Dialogs/DialogDownloadApp';
import RatingsReviews from '@/components/ShowMovies/RatingsReviews';

// HELP
import Hero from './help/Hero';

// STYLES
import { Wrapper, ProgressWrap } from './styles';

const showDownloadDialog = !store.get('not_show_dwnd_d');

class MovieDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      downloadAppDialog: showDownloadDialog,
    };
    this.params = getSearchParams(document.location.search);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    window.prerendercloudReady = false;

    this.getPageContent();
  }

  componentDidUpdate(prevProps) {
    const {
      router: {
        params: { movie_id },
      },
      isAuth,
      user,
      loading,
      data,
      addToDefWatchList,
    } = this.props;
    const awToken = this.params.get('aw');

    if (movie_id !== prevProps.router.params.movie_id || prevProps.isAuth !== isAuth) {
      this.getPageContent();
      window.scrollTo(0, 0);
    }

    // Add to watchlist if "aw" url param is present in url
    if (prevProps.loading !== loading && !!Object.keys(data).length && awToken) {
      const submitData = {
        from_url: !!awToken,
        url: window.location.href,
      };

      if (!isAuth) {
        submitData.user_token = awToken;
      }

      const analyticsProp = {
        user_id: user?.id || submitData.user_token || '',
        from_url: submitData.from_url,
      };

      addToDefWatchList(data.movie.id, submitData, () =>
        this.addToWatchListCallback(analyticsProp),
      );
    }
  }

  getPageContent = () => {
    const {
      router: {
        params: { movie_id },
        navigate,
      },
      getMovieDetails,
      getMovieStreamingData,
      getSimilarMovies,
      getContentReviews,
      reviewsMeta,
    } = this.props;
    const paginationData = {
      page: reviewsMeta.current_page,
      per_page: reviewsMeta.per_page,
    };

    getMovieDetails(movie_id, () => {
      navigate(routeEnhance(ROUTE_NO_APP_FOUND), { replace: true });
    });
    getMovieStreamingData(movie_id);
    getSimilarMovies(movie_id);
    getContentReviews(movie_id, paginationData);
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  handleSubmitClick = email => {
    const { data, subscribeToChanges } = this.props;

    subscribeToChanges({ email, content_id: data.movie.id });
  };

  addToWatchListCallback = (analyticsProps = {}) => {
    const {
      router: { navigate },
      data,
      user,
      partnerData,
    } = this.props;

    if (this.params.has('aw')) {
      this.params.delete('aw');

      navigate(
        {
          search: this.params.toString(),
        },
        { replace: true },
      );
    }

    const properties = {
      content_id: data.movie.id,
      content_name: data.movie.content_title,
      user_id: user?.id || '',
      partner_id: partnerData.id || '',
      location: LOCATIONS_MOVIE_DETAILS_PAGE,
      ...analyticsProps,
    };

    analyticsAddedToWatchlist(properties);
  };

  handleChangeWatchList = contentData => {
    const { openAddToWLDialogState } = this.props;

    openAddToWLDialogState({
      data: contentData,
      isSharedList: false,
      addCallback: this.addToWatchListCallback,
    });
  };

  handleCloseDownloadDialog = () => {
    this.setState({ downloadAppDialog: false });

    store.set('not_show_dwnd_d', true);
  };

  handleChangeReviewsPage = (e, page) => {
    const {
      router: {
        params: { movie_id },
      },
      getContentReviews,
      reviewsMeta,
    } = this.props;

    const paginationData = {
      page,
      per_page: reviewsMeta.per_page,
    };

    getContentReviews(movie_id, paginationData);
  };

  handleSubmitReview = data => {
    const {
      router: {
        params: { movie_id },
      },
      addContentReview,
      getMovieDetails,
    } = this.props;

    addContentReview(movie_id, data, () => {
      getMovieDetails(movie_id, () => {}, true);
    });
  };

  render() {
    const { downloadAppDialog } = this.state;
    const {
      t,
      router: {
        params: { movie_id },
      },
      isSimpleView,
      isAuth,
      data,
      loading,
      loadingSubscribe,
      partnerData,
      partnerSettings,
      parnterLoading,
      loadingActions,
      similarMovies,
      similarMoviesLoading,
      movieStreamingData,
      movieStreamingDataLoading,
      media,
      loadingMedia,
      changeContentStatus,
      downXs,
      loadingReviews,
      loadingAddReview,
      reviewsData,
      reviewsMeta,
      userReview,
    } = this.props;
    const isPartner = !!partnerData.slug;
    const hasData = !!Object.keys(data).length;
    const content = data.movie || {};

    if ((isPartner && !partnerSettings.search_movies_shows) || isSimpleView) {
      return <Redirect to={ROUTE_404} />;
    }

    const movieStreamingDataList = movieStreamingData
      ? [...(movieStreamingData.apps || []), ...(movieStreamingData['apps-rent-purchase'] || [])]
      : [];

    const notifyMeContent = (
      <NotifyMe
        title={t('movieDetails.notifyMe.title')}
        subtitle={t('movieDetails.notifyMe.subtitle')}
        loading={loadingSubscribe}
        onSubmit={this.handleSubmitClick}
        subscribed={isAuth && !!content?.subscription_status}
      />
    );

    const seoData = {
      canonicalUrl: `${siteUrl}${getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id })}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_SHOWS_AND_MOVIES_MOVIE, {
            partner: partnerData.slug,
            movie_id,
          })}`
        : `${siteUrl}${getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id })}`,
      title: content?.content_title || '',
      description: `MyBundle - ${content?.meta_description || content?.overview}`,
      image: content?.og_image_url
        ? getS3ImageUrl(`/og-image/content/${content.og_image_url}`)
        : content?.thumbnail || getS3ImageUrl('/images/watch-next-bg.jpg', '/assets'),
    };

    return (
      <Layout className="movie-details-layout">
        {hasData && !loading && (
          <Wrapper className="movie-details-page">
            {!parnterLoading && (
              <Helmet
                onChangeClientState={this.handleChangeHelmetData}
                script={[getOrgSchema('movie', content)]}
                titleTemplate="%s | MyBundle"
              >
                <title>{seoData.title}</title>
                <link rel="canonical" href={seoData.canonicalUrl} />
                <meta property="og:url" content={seoData.ogUrl} />
                <meta property="og:title" content={seoData.title} />
                <meta property="og:site_name" content="MyBundle" />
                <meta property="og:description" content={seoData.description} />
                <meta property="description" content={seoData.description} />
                {content?.meta_titles && <meta property="title" content={content.meta_titles} />}
                <meta property="og:type" content="website" />
                <meta property="fb:app_id" content="608514030003288" />
                <meta property="og:image" content={seoData.image} />
                <meta name="twitter:image" content={seoData.image} />
                <meta name="robots" content="index, follow, max-image-preview:large" />
                <meta name="twitter:title" content={seoData.title} />
                <meta name="twitter:description" content={seoData.description} />
                <meta name="twitter:site" content="@mybundletv" />
                <meta name="twitter:creator" content="@mybundletv" />
                <meta name="twitter:card" content="summary" />
              </Helmet>
            )}
            <Hero
              data={data}
              streamingData={movieStreamingData}
              streamingDataLoading={movieStreamingDataLoading}
              sameContent={data['movies-same-title']}
              partnerData={partnerData}
              loadingAction={loadingActions[content?.id]}
              addToWatchList={this.handleChangeWatchList}
              removeFromWatchList={this.handleChangeWatchList}
              changeContentStatus={changeContentStatus}
              isAuth={isAuth}
              analyticsLocation={LOCATIONS_MOVIE_DETAILS_PAGE}
            />
            {!!movieStreamingDataList.length && notifyMeContent}
            {!loadingMedia && <ContentGallery data={media} content={data.movie} />}
            <SimilarContent
              data={similarMovies}
              title={t('movieDetails.similarContent.title')}
              analyticsLocation={LOCATIONS_MOVIE_DETAILS_PAGE}
              loading={similarMoviesLoading}
            />
            <RatingsReviews
              name={data.movie?.content_title}
              reviewWasLeft={!!userReview}
              itemRating={data.movie?.average_user_rating || 0}
              reviews={reviewsData}
              reviewsMeta={reviewsMeta}
              onChangeReviewsPage={this.handleChangeReviewsPage}
              onSubmitReview={this.handleSubmitReview}
              analyticsData={{ movie_id, location: LOCATIONS_MOVIE_DETAILS_PAGE }}
              loading={loadingReviews || loadingAddReview}
            />
            {!movieStreamingDataList.length && notifyMeContent}
          </Wrapper>
        )}
        {loading && (
          <ProgressWrap>
            <CircularProgress />
          </ProgressWrap>
        )}
        {downXs && (
          <DialogDownloadApp open={downloadAppDialog} onClose={this.handleCloseDownloadDialog} />
        )}
      </Layout>
    );
  }
}

MovieDetails.propTypes = {
  t: PropTypes.func.isRequired,
  isSimpleView: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  user: PropTypes.object,
  count_content: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  similarMovies: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  movieStreamingData: PropTypes.object,
  media: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  movieStreamingDataLoading: PropTypes.bool.isRequired,
  similarMoviesLoading: PropTypes.bool.isRequired,
  loadingSubscribe: PropTypes.bool.isRequired,
  loadingMedia: PropTypes.bool.isRequired,
  reviewsData: PropTypes.array.isRequired,
  userReview: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  reviewsMeta: PropTypes.object.isRequired,
  loadingReviews: PropTypes.bool.isRequired,
  loadingAddReview: PropTypes.bool.isRequired,
  loadingActions: PropTypes.object.isRequired,
  getMovieDetails: PropTypes.func.isRequired,
  getMovieStreamingData: PropTypes.func.isRequired,
  getSimilarMovies: PropTypes.func.isRequired,
  partnerData: PropTypes.object.isRequired,
  partnerSettings: PropTypes.object.isRequired,
  subscribeToChanges: PropTypes.func.isRequired,
  parnterLoading: PropTypes.bool.isRequired,
  addToDefWatchList: PropTypes.func.isRequired,
  openAddToWLDialogState: PropTypes.func.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
  getContentReviews: PropTypes.func.isRequired,
  addContentReview: PropTypes.func.isRequired,
  manageProfDrawerState: PropTypes.func.isRequired,
  downXs: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ app, auth, movieDetails, partner, watchList, content }) => ({
  isSimpleView: app.isSimpleView,
  isAuth: auth.isAuth,
  user: auth.user,
  count_content: (auth.user && auth.user.content_count) || {},
  data: movieDetails.data,
  similarMovies: movieDetails.similarMovies,
  movieStreamingData: movieDetails.streamingData,
  media: movieDetails.media,
  loading: movieDetails.loadings.data,
  movieStreamingDataLoading: movieDetails.loadings.streamingData,
  similarMoviesLoading: movieDetails.loadings.similarMovies,
  loadingSubscribe: movieDetails.loadings.subscribe,
  loadingMedia: content.loadings.media,
  reviewsData: content.reviews.data || [],
  userReview: content.reviews.user_review,
  reviewsMeta: content.reviews.meta || {},
  loadingReviews: content.loadings.reviews,
  loadingAddReview: content.loadings.addReview,
  partnerData: partner.data,
  partnerSettings: partner.data?.settings || {},
  parnterLoading: partner.loadings.partner,
  loadingActions: { ...watchList.loadings.actions, ...content.loadings.actions },
});

const mapDispatchToProps = dispatch => ({
  getMovieDetails: (movie_id, onError, clearCache) => {
    dispatch(MovieDetailsActions.getMovieDetails.action({ movie_id, onError, clearCache }));
  },
  getMovieStreamingData: movie_id => {
    dispatch(MovieDetailsActions.getMovieStreamingData.action({ movie_id }));
  },
  getSimilarMovies: movie_id => {
    dispatch(MovieDetailsActions.getSimilarMovies.action({ movie_id }));
  },
  subscribeToChanges: data => {
    dispatch(MovieDetailsActions.subscribeToChanges.action({ data }));
  },
  addToDefWatchList: (content_id, data, onSuccess) => {
    dispatch(
      WatchListActions.addToDefWatchList.action({
        content_id,
        data,
        onSuccess,
      }),
    );
  },
  openAddToWLDialogState: dialogProps => {
    dispatch(WatchListActions.manageAddToWLDialogState.action({ state: true, props: dialogProps }));
  },
  changeContentStatus: (content_id, status) => {
    dispatch(
      ContentActions.changeContentStatus.action({
        content_id,
        status,
      }),
    );
  },
  getContentReviews: (content_id, params = {}) => {
    dispatch(
      ContentActions.getContentReviews.action({
        content_id,
        params,
      }),
    );
  },
  addContentReview: (content_id, data = {}, onSuccess) => {
    dispatch(
      ContentActions.addContentReview.action({
        content_id,
        data,
        onSuccess,
      }),
    );
  },
  manageProfDrawerState: (state, props) => {
    dispatch(AppActions.manageProfDrawerState.action({ state, props }));
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
  withMediaQuery({ downXs: theme => theme.breakpoints.down('xs') }),
)(MovieDetails);
